import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../lib/supabase';

export type Role = 'Admin' | 'Employee';

export interface Tenant {
  id: string;
  name: string;
  themeColor?: string;
  logoUrl?: string;
  activeModules: string[];
  geminiApiKey?: string;
  marginThresholdGreen?: number;
  marginThresholdYellow?: number;
  address?: string;
  street?: string;
  city?: string;
  zip?: string;
  vatId?: string;
}

export type VacationStatus = 'pending' | 'approved' | 'rejected';

export interface VacationRequest {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  status: VacationStatus;
}

export interface TimeCorrectionRequest {
  id: string;
  userId: string;
  date: string;
  oldStart: string;
  oldEnd: string;
  newStart: string;
  newEnd: string;
  reason: string;
  status: VacationStatus;
}

export interface CompDayRequest {
  id: string;
  userId: string;
  date: string;
  status: VacationStatus;
}

export interface User {
  id: string;
  name: string;
  username: string;
  password: string;
  mustChangePassword: boolean;
  role: Role;
  tenantId: string;
  activeModules: string[];

  // Zeiterfassung live values
  isWorking?: boolean;
  isOnBreak?: boolean;
  workStartTime?: string | null;
  breakStartTime?: string | null;
  accumulatedWorkSeconds?: number;
  accumulatedBreakSeconds?: number;
  canSeeFinancials?: boolean;
  isPlatformAdmin?: boolean;

  // Vacation & Flex Time
  vacationTotal: number;
  vacationTaken: number;
  flexTimeMinutes: number;

  // Customizable Sollzeit (daily target hours)
  targetHoursMonday?: number;
  targetHoursTuesday?: number;
  targetHoursWednesday?: number;
  targetHoursThursday?: number;
  targetHoursFriday?: number;
  targetHoursSaturday?: number;
  targetHoursSunday?: number;
}

// Passwörter werden NICHT im Client gespeichert. Auth läuft ausschließlich über Supabase Auth.
const DEFAULT_PASSWORD = 'Start2026'; // Standard-Passwort für neu angelegte User

// Helper: check if a role has management privileges
export const isManager = (role: Role) => role === 'Admin';

export type LoginResult = { success: true; user: User; tenant: Tenant; needsPasswordChange: boolean } | { success: false; error: string };

interface AuthContextType {
  currentUser: User | null;
  currentTenant: Tenant | null;
  isPlatformAdmin: boolean;
  switchTenant: (tenantId: string) => void;
  loginWithCredentials: (username: string, password: string) => Promise<LoginResult>;
  confirmLogin: (user: User, tenant: Tenant) => void;
  logout: () => void;
  changePassword: (userId: string, newPass: string) => Promise<void>;

  // Mutating functions
  users: User[];
  tenants: Tenant[];
  updateTenantName: (newName: string) => void;
  updateTenantModules: (tenantId: string, modules: string[]) => Promise<void>;
  updateTenantMargins: (green: number, yellow: number) => Promise<void>;
  updateTenantGeminiKey: (apiKey: string) => Promise<void>;
  addUser: (name: string, username: string, role: Role, activeModules: string[]) => void;
  createEmployeeLogin: (name: string, email: string, password: string) => Promise<string | null>;
  updateUserRole: (userId: string, newRole: Role) => void;
  updateUserModules: (userId: string, modules: string[]) => void;
  updateUserFinancials: (userId: string, canSeeFinancials: boolean) => void;

  resetPassword: (userId: string) => void;

  // Timesheet
  updateUserTimesheet: (userId: string, data: Partial<User>) => void;

  // Vacation
  vacationRequests: VacationRequest[];
  createVacationRequest: (userId: string, startDate: string, endDate: string) => void;
  updateVacationRequestStatus: (requestId: string, status: VacationStatus) => void;
  editVacationRequest: (requestId: string, startDate: string, endDate: string) => void;
  deleteVacationRequest: (requestId: string) => void;

  // Time Corrections
  timeCorrectionRequests: TimeCorrectionRequest[];
  createTimeCorrectionRequest: (req: Omit<TimeCorrectionRequest, 'id' | 'status'>) => void;
  updateTimeCorrectionStatus: (requestId: string, status: VacationStatus) => void;
  deleteTimeCorrectionRequest: (requestId: string) => void;

  // Comp Days (Gleittage)
  compDayRequests: CompDayRequest[];
  createCompDayRequest: (userId: string, date: string) => void;
  updateCompDayStatus: (requestId: string, status: VacationStatus) => void;
  editCompDayRequest: (requestId: string, date: string) => void;
  deleteCompDayRequest: (requestId: string) => void;

  isLoading: boolean;
}

// Initial Mock Data (Fallback)
const ALL_MODULES = ['angebote', 'karte', 'lager', 'maschinen', 'zeiterfassung', 'crm', 'rechnungen', 'auftraege', 'fuhrpark', 'einsatzplan', 'mitarbeiter', 'protokoll', 'verwaltung', 'lieferanten', 'ki'];

export const INITIAL_MOCK_USERS: User[] = [
  {
    id: 'U-101', name: 'Thomas Fieg', username: 'tfieg', password: '', mustChangePassword: false,
    role: 'Admin', tenantId: 'T-100', activeModules: ALL_MODULES,
    vacationTotal: 30, vacationTaken: 0, flexTimeMinutes: 480, canSeeFinancials: true
  },
  {
    id: 'U-100', name: 'System Admin', username: 'admin', password: DEFAULT_PASSWORD, mustChangePassword: false,
    role: 'Admin', tenantId: 'T-100', activeModules: ALL_MODULES,
    vacationTotal: 30, vacationTaken: 0, flexTimeMinutes: 0, canSeeFinancials: false
  }
];

export const INITIAL_TENANTS: Tenant[] = [
  {
    id: 'T-100',
    name: 'Betriebszentrale',
    themeColor: '#4A6FA5',
    logoUrl: '/logo.png',
    activeModules: ALL_MODULES,
    marginThresholdGreen: 25.0,
    marginThresholdYellow: 10.0
  }
];


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenants, setTenants] = useState<Tenant[]>(INITIAL_TENANTS);
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
  const [timeCorrectionRequests, setTimeCorrectionRequests] = useState<TimeCorrectionRequest[]>([]);
  const [compDayRequests, setCompDayRequests] = useState<CompDayRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const isPlatformAdmin = currentUser?.isPlatformAdmin || false;

  useEffect(() => {
    const fetchUserProfile = async (authUserId: string) => {
      const { data: userProfile } = await supabase.from('users').select('*').eq('auth_id', authUserId).single();
      if (userProfile) {
        const liveUser: User = {
          id: userProfile.id,
          name: userProfile.name,
          username: userProfile.username,
          password: '',
          mustChangePassword: userProfile.must_change_password,
          role: userProfile.role as Role,
          tenantId: userProfile.tenant_id,
          activeModules: Array.isArray(userProfile.active_modules) ? userProfile.active_modules : (typeof userProfile.active_modules === 'string' ? userProfile.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : []),
          vacationTotal: userProfile.vacation_total || 0,
          vacationTaken: userProfile.vacation_taken || 0,
          flexTimeMinutes: userProfile.flex_time_minutes || 0,
          isWorking: userProfile.is_working || false,
          isOnBreak: userProfile.is_on_break || false,
          workStartTime: userProfile.work_start_time || null,
          breakStartTime: userProfile.break_start_time || null,
          accumulatedWorkSeconds: userProfile.accumulated_work_seconds || 0,
          accumulatedBreakSeconds: userProfile.accumulated_break_seconds || 0,
          canSeeFinancials: userProfile.can_see_financials || false,
          isPlatformAdmin: userProfile.is_platform_admin || false,
          targetHoursMonday: userProfile.target_hours_monday !== undefined ? Number(userProfile.target_hours_monday) : 8.0,
          targetHoursTuesday: userProfile.target_hours_tuesday !== undefined ? Number(userProfile.target_hours_tuesday) : 8.0,
          targetHoursWednesday: userProfile.target_hours_wednesday !== undefined ? Number(userProfile.target_hours_wednesday) : 8.0,
          targetHoursThursday: userProfile.target_hours_thursday !== undefined ? Number(userProfile.target_hours_thursday) : 8.0,
          targetHoursFriday: userProfile.target_hours_friday !== undefined ? Number(userProfile.target_hours_friday) : 8.0,
          targetHoursSaturday: userProfile.target_hours_saturday !== undefined ? Number(userProfile.target_hours_saturday) : 0.0,
          targetHoursSunday: userProfile.target_hours_sunday !== undefined ? Number(userProfile.target_hours_sunday) : 0.0
        };
        setCurrentUser(liveUser);
        const { data: tDataForUser } = await supabase.from('tenants').select('*').eq('id', userProfile.tenant_id).single();
        if (tDataForUser) {
          const liveTenant = { 
            id: tDataForUser.id, 
            name: tDataForUser.name, 
            themeColor: tDataForUser.theme_color, 
            logoUrl: tDataForUser.logo_url,
            activeModules: Array.isArray(tDataForUser.active_modules) ? tDataForUser.active_modules : (typeof tDataForUser.active_modules === 'string' ? tDataForUser.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : []),
            geminiApiKey: tDataForUser.gemini_api_key,
            marginThresholdGreen: tDataForUser.margin_threshold_green !== undefined ? Number(tDataForUser.margin_threshold_green) : 25.0,
            marginThresholdYellow: tDataForUser.margin_threshold_yellow !== undefined ? Number(tDataForUser.margin_threshold_yellow) : 10.0
          };
          setCurrentTenant(liveTenant);
          if (liveTenant.themeColor) document.documentElement.style.setProperty('--sys-color-primary', liveTenant.themeColor);
        }
      }
    };

    const loadInitialData = async () => {
      // 0. Vorab lokalen Session-Cache prüfen für sofortigen Start
      try {
        const storedUser = localStorage.getItem('sz_auth_user');
        const storedTenant = localStorage.getItem('sz_auth_tenant');
        if (storedUser && storedTenant) {
          const parsedUser = JSON.parse(storedUser);
          const parsedTenant = JSON.parse(storedTenant);
          setCurrentUser({ ...parsedUser, mustChangePassword: false });
          setCurrentTenant(parsedTenant);
          if (parsedTenant.themeColor) {
            document.documentElement.style.setProperty('--sys-color-primary', parsedTenant.themeColor);
          }
        }
      } catch (e) {
        console.warn('LocalStorage load failed:', e);
      }

      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 2000));
      
      const realLoad = async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session?.user?.id) {
            await fetchUserProfile(sessionData.session.user.id);
          }
          
          // Fetch tenants, users, and vacations in parallel
          const [tenantsRes, usersRes, vacRes] = await Promise.allSettled([
            supabase.from('tenants').select('*'),
            supabase.from('users').select('*'),
            supabase.from('vacation_requests').select('*')
          ]);

          if (tenantsRes.status === 'fulfilled' && tenantsRes.value.data && tenantsRes.value.data.length > 0) {
            setTenants(tenantsRes.value.data.map(t => ({
              id: t.id,
              name: t.name,
              themeColor: t.theme_color,
              logoUrl: t.logo_url,
              activeModules: Array.isArray(t.active_modules) ? t.active_modules : (typeof t.active_modules === 'string' ? t.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : []),
              geminiApiKey: t.gemini_api_key,
              marginThresholdGreen: t.margin_threshold_green !== undefined ? Number(t.margin_threshold_green) : 25.0,
              marginThresholdYellow: t.margin_threshold_yellow !== undefined ? Number(t.margin_threshold_yellow) : 10.0
            })));
          }

          if (usersRes.status === 'fulfilled' && usersRes.value.data && usersRes.value.data.length > 0) {
            setUsers(usersRes.value.data.map(u => ({
              id: u.id,
              name: u.name,
              username: u.username,
              password: '',
              mustChangePassword: false,
              role: u.role as Role,
              tenantId: u.tenant_id,
              activeModules: Array.isArray(u.active_modules) ? u.active_modules : (typeof u.active_modules === 'string' ? u.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : []),
              vacationTotal: u.vacation_total || 0,
              vacationTaken: u.vacation_taken || 0,
              flexTimeMinutes: u.flex_time_minutes || 0,
              isWorking: u.is_working || false,
              isOnBreak: u.is_on_break || false,
              workStartTime: u.work_start_time || null,
              breakStartTime: u.break_start_time || null,
              accumulatedWorkSeconds: u.accumulated_work_seconds || 0,
              accumulatedBreakSeconds: u.accumulated_break_seconds || 0,
              canSeeFinancials: u.can_see_financials || false,
              targetHoursMonday: u.target_hours_monday !== undefined ? Number(u.target_hours_monday) : 8.0,
              targetHoursTuesday: u.target_hours_tuesday !== undefined ? Number(u.target_hours_tuesday) : 8.0,
              targetHoursWednesday: u.target_hours_wednesday !== undefined ? Number(u.target_hours_wednesday) : 8.0,
              targetHoursThursday: u.target_hours_thursday !== undefined ? Number(u.target_hours_thursday) : 8.0,
              targetHoursFriday: u.target_hours_friday !== undefined ? Number(u.target_hours_friday) : 8.0,
              targetHoursSaturday: u.target_hours_saturday !== undefined ? Number(u.target_hours_saturday) : 0.0,
              targetHoursSunday: u.target_hours_sunday !== undefined ? Number(u.target_hours_sunday) : 0.0
            })));
          }

          if (vacRes.status === 'fulfilled' && Array.isArray(vacRes.value.data)) {
            setVacationRequests(vacRes.value.data.map(v => ({
              id: v.id || `v-err-${Math.random()}`,
              userId: v.user_id || '',
              startDate: v.start_date || '',
              endDate: v.end_date || '',
              status: (v.status || 'pending') as VacationStatus
            })));
          }
        } catch (error) {
          console.warn('Supabase initial load partially failed:', error);
        }
      };

      // Race between network load and 2s timeout
      await Promise.race([realLoad(), timeoutPromise]);
      setIsLoading(false);
    };

    loadInitialData();

    // Supabase Auth State Change Listener für Token-Refresh & Session-Sync
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user?.id) {
          await fetchUserProfile(session.user.id);
        }
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setCurrentTenant(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Authenticate with username + password securely via Supabase Auth
  // Supports both legacy usernames (e.g. "tfieg" → tfieg@betriebszentrale.de)
  // and real email addresses (e.g. "mueller@firma.de") for new tenants
  const loginWithCredentials = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const input = username.trim().toLowerCase();
      // If the input contains @, treat it as a real email; otherwise append @betriebszentrale.de
      const email = input.includes('@') ? input : `${input}@betriebszentrale.de`;
      
      const timeoutPromise = new Promise<{ data: any; error: any }>((_, reject) =>
        setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 3500)
      );

      let authData: any = null;
      let authError: any = null;

      try {
        const authRes: any = await Promise.race([
          supabase.auth.signInWithPassword({ email, password }),
          timeoutPromise
        ]);
        authData = authRes?.data;
        authError = authRes?.error;
      } catch (err: any) {
        authError = { message: err?.message === 'AUTH_TIMEOUT' ? 'Verbindung zeitweise verzögert.' : err?.message };
      }
      
      if (authError || !authData?.user) {
        console.warn('Supabase Auth fehlgeschlagen:', authError);
        
        const hint = !input.includes('@') ? ' (Tipp: Verwenden Sie bei eigener Firmen-E-Mail Ihre vollständige E-Mail-Adresse)' : '';
        const msg = authError?.message === 'Invalid login credentials' 
          ? 'Ungültige Anmeldedaten. Bitte überprüfen Sie Benutzername und Passwort.'
          : (authError?.message || 'Login fehlgeschlagen');
        return { success: false, error: msg + hint };
      }

      // Load Profile
      let activeProfile: any = null;
      try {
        const { data: userProfile } = await supabase.from('users').select('*').eq('auth_id', authData.user.id).maybeSingle();
        activeProfile = userProfile;
      } catch (pErr) {
        console.warn('Profil per auth_id nicht gefunden:', pErr);
      }

      if (!activeProfile) {
        const fallbackUsername = input.split('@')[0];
        try {
          const { data: fbProfile } = await supabase.from('users').select('*').eq('username', fallbackUsername).maybeSingle();
          activeProfile = fbProfile;
        } catch (fbErr) {
          console.warn('Profil per username nicht gefunden:', fbErr);
        }
      }

      const user: User = activeProfile ? {
        id: activeProfile.id,
        name: activeProfile.name || input.split('@')[0],
        username: activeProfile.username || input.split('@')[0],
        password: '',
        mustChangePassword: false,
        role: (activeProfile.role as Role) || 'Admin',
        tenantId: activeProfile.tenant_id || 'T-100',
        activeModules: Array.isArray(activeProfile.active_modules) ? activeProfile.active_modules : (typeof activeProfile.active_modules === 'string' ? activeProfile.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : ALL_MODULES),
        vacationTotal: activeProfile.vacation_total || 30,
        vacationTaken: activeProfile.vacation_taken || 0,
        flexTimeMinutes: activeProfile.flex_time_minutes || 0,
        isWorking: activeProfile.is_working || false,
        isOnBreak: activeProfile.is_on_break || false,
        workStartTime: activeProfile.work_start_time || null,
        breakStartTime: activeProfile.break_start_time || null,
        accumulatedWorkSeconds: activeProfile.accumulated_work_seconds || 0,
        accumulatedBreakSeconds: activeProfile.accumulated_break_seconds || 0,
        canSeeFinancials: activeProfile.can_see_financials || false,
        isPlatformAdmin: activeProfile.is_platform_admin || false,
        targetHoursMonday: activeProfile.target_hours_monday !== undefined ? Number(activeProfile.target_hours_monday) : 8.0,
        targetHoursTuesday: activeProfile.target_hours_tuesday !== undefined ? Number(activeProfile.target_hours_tuesday) : 8.0,
        targetHoursWednesday: activeProfile.target_hours_wednesday !== undefined ? Number(activeProfile.target_hours_wednesday) : 8.0,
        targetHoursThursday: activeProfile.target_hours_thursday !== undefined ? Number(activeProfile.target_hours_thursday) : 8.0,
        targetHoursFriday: activeProfile.target_hours_friday !== undefined ? Number(activeProfile.target_hours_friday) : 8.0,
        targetHoursSaturday: activeProfile.target_hours_saturday !== undefined ? Number(activeProfile.target_hours_saturday) : 0.0,
        targetHoursSunday: activeProfile.target_hours_sunday !== undefined ? Number(activeProfile.target_hours_sunday) : 0.0
      } : {
        id: authData.user.id,
        name: authData.user.email?.split('@')[0] || input,
        username: input.split('@')[0],
        password: '',
        mustChangePassword: false,
        role: 'Admin',
        tenantId: 'T-100',
        activeModules: ALL_MODULES,
        vacationTotal: 30,
        vacationTaken: 0,
        flexTimeMinutes: 0,
        canSeeFinancials: true
      };

      // Load Tenant for this user
      let tenant: Tenant = INITIAL_TENANTS[0];
      try {
        const { data: tData } = await supabase.from('tenants').select('*').eq('id', user.tenantId).maybeSingle();
        if (tData) {
          tenant = {
            id: tData.id,
            name: tData.name,
            themeColor: tData.theme_color || '#0078d4',
            logoUrl: tData.logo_url || '/logo.png',
            activeModules: Array.isArray(tData.active_modules) ? tData.active_modules : (typeof tData.active_modules === 'string' ? tData.active_modules.replace(/[{}]/g, '').replace(/"/g, '').split(',').filter(Boolean).map((s: string) => s.trim()) : ALL_MODULES),
            geminiApiKey: tData.gemini_api_key,
            marginThresholdGreen: tData.margin_threshold_green !== undefined ? Number(tData.margin_threshold_green) : 25.0,
            marginThresholdYellow: tData.margin_threshold_yellow !== undefined ? Number(tData.margin_threshold_yellow) : 10.0
          };
        }
      } catch (tErr) {
        console.warn('Tenant nicht per DB geladen, nutze Standard:', tErr);
      }

      return { success: true, user, tenant, needsPasswordChange: false };
    } catch(e: any) {
      return { success: false, error: e?.message || 'Ein Fehler beim Anmelden ist aufgetreten.' };
    }
  };

  const confirmLogin = (user: User, tenant: Tenant) => {
    const cleanUser = { ...user, mustChangePassword: false };
    try {
      localStorage.setItem('sz_auth_user', JSON.stringify(cleanUser));
      localStorage.setItem('sz_auth_tenant', JSON.stringify(tenant));
    } catch (e) {
      console.warn('LocalStorage save failed:', e);
    }
    setCurrentUser(cleanUser);
    setCurrentTenant(tenant);
    setUsers(prev => prev.some(u => u.id === cleanUser.id) ? prev.map(u => u.id === cleanUser.id ? cleanUser : u) : [...prev, cleanUser]);
    setTenants(prev => prev.some(t => t.id === tenant.id) ? prev.map(t => t.id === tenant.id ? tenant : t) : [...prev, tenant]);
    if (tenant.themeColor) {
      document.documentElement.style.setProperty('--sys-color-primary', tenant.themeColor);
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('sz_auth_user');
      localStorage.removeItem('sz_auth_tenant');
    } catch (e) {}
    await supabase.auth.signOut();
    setCurrentUser(null);
    setCurrentTenant(null);
    window.location.href = '/login';
  };

  // SuperAdmin: Zwischen Mandanten wechseln
  const switchTenant = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
      if (tenant.themeColor) {
        document.documentElement.style.setProperty('--sys-color-primary', tenant.themeColor);
      }
    }
  };

  const changePassword = async (userId: string, newPassword: string) => {
    // 1. Versuche Supabase Auth Passwort zu aktualisieren (falls Session vorhanden)
    try {
      const timeoutPromise = new Promise<{ error: any }>((_, reject) => 
        setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 3500)
      );
      await Promise.race([
        supabase.auth.updateUser({ password: newPassword }),
        timeoutPromise
      ]);
    } catch (err: any) {
      console.warn('Supabase auth.updateUser nicht möglich oder verzögert (lokaler/Mock-Modus):', err?.message);
    }

    // 2. State & Mock-User aktualisieren
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, password: '', mustChangePassword: false } : u));
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, password: '', mustChangePassword: false } : null);
    }

    // 3. INITIAL_MOCK_USERS im Speicher aktualisieren
    const mockIdx = INITIAL_MOCK_USERS.findIndex(u => u.id === userId || u.username.toLowerCase() === 'admin');
    if (mockIdx >= 0) {
      INITIAL_MOCK_USERS[mockIdx].mustChangePassword = false;
      INITIAL_MOCK_USERS[mockIdx].password = newPassword;
    }

    // 4. Datenbank-Tabelle aktualisieren
    try {
      await supabase.from('users').update({ must_change_password: false }).eq('id', userId);
    } catch (dbErr) {
      console.warn('users table update failed:', dbErr);
    }
  };

  const resetPassword = async (userId: string) => {
    // Finde den Nutzer
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    try {
      // 1. Rufe die sichere RPC-Funktion auf, die das Passwort hart auf "Start2026" überschreibt
      const { error } = await supabase.rpc('admin_reset_user_password', { 
        internal_user_id: userId, 
        new_password: 'Start2026' 
      });
      
      if (error) {
        console.error("RPC Error:", error);
        alert('Fehler beim Zurücksetzen: ' + error.message);
        return;
      }

      // 2. State updaten
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, mustChangePassword: true } : u));
      alert(`Das Passwort für ${user.name} wurde erfolgreich auf "Start2026" zurückgesetzt.`);
    } catch (err: any) {
      console.error(err);
      alert('Fehler beim Zurücksetzen.');
    }
  };

  const updateTenantName = async (newName: string) => {
    if (currentTenant && isPlatformAdmin) {
      await supabase.from('tenants').update({ name: newName }).eq('id', currentTenant.id);
      setCurrentTenant({ ...currentTenant, name: newName });
      setTenants(tenants.map(t => t.id === currentTenant.id ? { ...t, name: newName } : t));
    }
  };

  const updateTenantModules = async (tenantId: string, modules: string[]) => {
    if (!isPlatformAdmin) return;
    const { error } = await supabase.from('tenants').update({ active_modules: modules }).eq('id', tenantId);
    if (!error) {
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, activeModules: modules } : t));
      if (currentTenant?.id === tenantId) {
        setCurrentTenant(prev => prev ? { ...prev, activeModules: modules } : null);
      }
    }
  };

  const updateTenantMargins = async (green: number, yellow: number) => {
    if (currentTenant) {
      const { error } = await supabase
        .from('tenants')
        .update({ margin_threshold_green: green, margin_threshold_yellow: yellow })
        .eq('id', currentTenant.id);

      if (error) {
        console.error("Error updating tenant margin thresholds:", error);
        throw error;
      }

      const updatedTenant = {
        ...currentTenant,
        marginThresholdGreen: green,
        marginThresholdYellow: yellow
      };
      setCurrentTenant(updatedTenant);
      setTenants(prev => prev.map(t => t.id === currentTenant.id ? updatedTenant : t));
    }
  };

  const updateTenantGeminiKey = async (apiKey: string) => {
    if (!currentTenant) return;
    const cleanKey = apiKey.trim();

    // 1. Sofort im React State & LocalStorage persistieren für verzögerungsfreie UI
    const updatedTenant = {
      ...currentTenant,
      geminiApiKey: cleanKey
    };
    setCurrentTenant(updatedTenant);
    try {
      localStorage.setItem('sz_auth_tenant', JSON.stringify(updatedTenant));
    } catch (e) {
      console.warn('LocalStorage save failed:', e);
    }
    setTenants(prev => prev.map(t => t.id === currentTenant.id ? updatedTenant : t));

    // 2. In Supabase mit Timeout absichern (damit die UI nie blockiert)
    try {
      const updatePromise = supabase
        .from('tenants')
        .update({ gemini_api_key: cleanKey })
        .eq('id', currentTenant.id);

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout bei Supabase')), 5000)
      );

      const res = await Promise.race([updatePromise, timeoutPromise]) as any;
      if (res?.error) {
        console.warn("Supabase update error (locally preserved):", res.error);
      }
    } catch (err) {
      console.warn("Supabase update error/timeout (locally preserved):", err);
    }
  };

  const addUser = async (name: string, username: string, role: Role, activeModules: string[]) => {
    if (currentTenant) {
      const email = `${username.trim().toLowerCase()}@betriebszentrale.de`;
      
      try {
        // Nutze die neue sichere RPC-Funktion, um den Nutzer bestätigt anzulegen
        const { data: newAuthId, error: rpcErr } = await supabase.rpc('admin_create_user', {
          new_email: email,
          new_password: DEFAULT_PASSWORD,
          new_name: name,
          new_username: username,
          new_role: role,
          new_tenant_id: currentTenant.id,
          new_active_modules: activeModules
        });

        if (rpcErr) {
          console.error("Fehler beim Erstellen des Nutzers (RPC):", rpcErr);
          alert('Fehler beim Anlegen: ' + rpcErr.message);
          return;
        }

        // Finde die echte public.users.id für die UUID
        let realUserId = `u-${Date.now()}`;
        if (newAuthId) {
          const { data: freshUser, error: freshErr } = await supabase
            .from('users')
            .select('id')
            .eq('auth_id', newAuthId)
            .single();
          if (freshUser?.id) {
            realUserId = freshUser.id;
          } else if (freshErr) {
            console.warn("Konnte echte User-ID nicht laden, verwende temporäre ID:", freshErr);
          }
        }

        // Lokaler State Update
        const newUser: User = {
          id: realUserId,
          name,
          username,
          password: '',
          mustChangePassword: true,
          role,
          tenantId: currentTenant.id,
          activeModules,
          vacationTotal: 30,
          vacationTaken: 0,
          flexTimeMinutes: 0
        };
        
        // Da die RPC-Funktion asynchron in der DB alles erledigt hat, 
        // laden wir die Nutzerliste am besten neu oder fügen den Nutzer basierend auf der RPC Rückgabe hinzu.
        // Für den Moment fügen wir ihn einfach zum lokalen State hinzu.
        setUsers(prev => [...prev, newUser]);
        alert(`Mitarbeiter ${name} wurde erfolgreich angelegt.`);
      } catch (err: any) {
        console.error("Unerwarteter Fehler:", err);
        alert('Ein unerwarteter Fehler ist aufgetreten.');
      }
    }
  };

  /**
   * Legt einen einfachen System-Login für einen Mitarbeiter an.
   * Gibt die interne user.id zurück (für die employee.user_id Verknüpfung),
   * oder null bei Fehler.
   */
  const createEmployeeLogin = async (name: string, email: string, password: string): Promise<string | null> => {
    if (!currentTenant) return null;
    // Punkte im Usernamen entfernen (merten.fieg → mertenfieg)
    const username = email.split('@')[0].toLowerCase().replace(/\./g, '').replace(/[^a-z0-9_]/g, '');
    try {
      const { data: newAuthId, error: rpcErr } = await supabase.rpc('admin_create_user', {
        new_email: email.trim().toLowerCase(),
        new_password: password,
        new_name: name,
        new_username: username,
        new_role: 'Employee',
        new_tenant_id: currentTenant.id,
        new_active_modules: ['auftraege', 'zeiterfassung', 'karte', 'lager', 'maschinen', 'fuhrpark', 'einsatzplan']
      });
      if (rpcErr) {
        console.error('createEmployeeLogin RPC error:', rpcErr);
        throw new Error(rpcErr.message || 'Unbekannter Fehler beim Anlegen des Logins.');
      }

      // RPC gibt die neue auth.users UUID zurück – damit den public.users Eintrag finden
      if (newAuthId) {
        const { data: freshUser } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', newAuthId)
          .single();
        if (freshUser?.id) return freshUser.id;
      }

      // Fallback: neuesten User des Tenants nehmen
      const { data: fallbackUser } = await supabase
        .from('users')
        .select('id')
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      return fallbackUser?.id ?? null;
    } catch (err: any) {
      console.error('createEmployeeLogin error:', err);
      throw err;
    }
  };

  const updateUserRole = async (userId: string, newRole: Role) => {
    setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, role: newRole } : null);
    }
    const { error } = await supabase.rpc('update_user_role', { p_user_id: userId, p_role: newRole });
    if (error) console.error("Error updating user role via RPC:", error);
  };

  const updateUserModules = async (userId: string, modules: string[]) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, activeModules: modules } : u));
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, activeModules: modules } : null);
    }
    // SECURITY DEFINER RPC umgeht RLS-Blockierungen beim Modul-Update
    const { error } = await supabase.rpc('update_user_modules', {
      target_user_id: userId,
      new_modules: modules
    });
    if (error) {
      console.error('Fehler beim Aktualisieren der Module:', error);
      alert('Fehler beim Speichern: ' + error.message);
    }
  };

  const updateUserFinancials = async (userId: string, canSeeFinancials: boolean) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, canSeeFinancials } : u));
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, canSeeFinancials } : null);
    }
    const { error } = await supabase.rpc('update_user_financials', { p_user_id: userId, p_can_see: canSeeFinancials });
    if (error) {
      console.error("Error updating financials flag via RPC:", error);
      alert("Fehler beim Speichern der Berechtigung: " + error.message);
      
      // Fallback: Versuche direkten Update, falls RPC nicht existiert
      const fallback = await supabase.from('users').update({ can_see_financials: canSeeFinancials }).eq('id', userId);
      if (fallback.error) {
        alert("Auch direkter Fallback fehlgeschlagen: " + fallback.error.message);
      } else {
        alert("Erfolg über Fallback-Update!");
      }
    }
  };

  const updateUserTimesheet = async (userId: string, data: Partial<User>) => {
    setUsers(users.map(u => u.id === userId ? { ...u, ...data } : u));
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, ...data } : null);
    }

    // Map fields back to SQL columns
    const updateData: any = {};
    if (data.isWorking !== undefined) updateData.is_working = data.isWorking;
    if (data.isOnBreak !== undefined) updateData.is_on_break = data.isOnBreak;
    if (data.workStartTime !== undefined) updateData.work_start_time = data.workStartTime;
    if (data.breakStartTime !== undefined) updateData.break_start_time = data.breakStartTime;
    if (data.accumulatedWorkSeconds !== undefined) updateData.accumulated_work_seconds = data.accumulatedWorkSeconds;
    if (data.accumulatedBreakSeconds !== undefined) updateData.accumulated_break_seconds = data.accumulatedBreakSeconds;
    if (data.vacationTaken !== undefined) updateData.vacation_taken = data.vacationTaken;
    if (data.flexTimeMinutes !== undefined) updateData.flex_time_minutes = data.flexTimeMinutes;
    if (data.targetHoursMonday !== undefined) updateData.target_hours_monday = data.targetHoursMonday;
    if (data.targetHoursTuesday !== undefined) updateData.target_hours_tuesday = data.targetHoursTuesday;
    if (data.targetHoursWednesday !== undefined) updateData.target_hours_wednesday = data.targetHoursWednesday;
    if (data.targetHoursThursday !== undefined) updateData.target_hours_thursday = data.targetHoursThursday;
    if (data.targetHoursFriday !== undefined) updateData.target_hours_friday = data.targetHoursFriday;
    if (data.targetHoursSaturday !== undefined) updateData.target_hours_saturday = data.targetHoursSaturday;
    if (data.targetHoursSunday !== undefined) updateData.target_hours_sunday = data.targetHoursSunday;

    await supabase.from('users').update(updateData).eq('id', userId);
  };

  const createVacationRequest = async (userId: string, startDate: string, endDate: string) => {
    const id = `v-${Date.now()}`;
    setVacationRequests(prev => [...prev, { id, userId, startDate, endDate, status: 'pending' }]);
    await supabase.from('vacation_requests').insert({
      id,
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
      status: 'pending'
    });
  };

  const updateVacationRequestStatus = async (requestId: string, status: VacationStatus) => {
    setVacationRequests(prev => prev.map(v => v.id === requestId ? { ...v, status } : v));
    await supabase.from('vacation_requests').update({ status }).eq('id', requestId);
  };

  const editVacationRequest = async (requestId: string, startDate: string, endDate: string) => {
    setVacationRequests(prev => prev.map(v => v.id === requestId ? { ...v, startDate, endDate } : v));
    await supabase.from('vacation_requests').update({ start_date: startDate, end_date: endDate }).eq('id', requestId);
  };

  const deleteVacationRequest = async (requestId: string) => {
    setVacationRequests(prev => prev.filter(v => v.id !== requestId));
    await supabase.from('vacation_requests').delete().eq('id', requestId);
  };

  const createTimeCorrectionRequest = async (req: Omit<TimeCorrectionRequest, 'id' | 'status'>) => {
    const id = `tc-${Date.now()}`;
    setTimeCorrectionRequests(prev => [...prev, { ...req, id, status: 'pending' }]);
    await supabase.from('time_correction_requests').insert({
      id,
      user_id: req.userId,
      date: req.date,
      old_start: req.oldStart,
      old_end: req.oldEnd,
      new_start: req.newStart,
      new_end: req.newEnd,
      reason: req.reason,
      status: 'pending'
    });
  };

  const updateTimeCorrectionStatus = async (requestId: string, status: VacationStatus) => {
    setTimeCorrectionRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));
    await supabase.from('time_correction_requests').update({ status }).eq('id', requestId);
  };

  const deleteTimeCorrectionRequest = async (requestId: string) => {
    setTimeCorrectionRequests(prev => prev.filter(r => r.id !== requestId));
    await supabase.from('time_correction_requests').delete().eq('id', requestId);
  };

  const createCompDayRequest = async (userId: string, date: string) => {
    const id = `cd-${Date.now()}`;
    setCompDayRequests(prev => [...prev, { id, userId, date, status: 'pending' }]);
    await supabase.from('comp_day_requests').insert({
      id,
      user_id: userId,
      date,
      status: 'pending'
    });
  };

  const updateCompDayStatus = async (requestId: string, status: VacationStatus) => {
    setCompDayRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));
    await supabase.from('comp_day_requests').update({ status }).eq('id', requestId);
  };

  const editCompDayRequest = async (requestId: string, date: string) => {
    setCompDayRequests(prev => prev.map(r => r.id === requestId ? { ...r, date } : r));
    await supabase.from('comp_day_requests').update({ date }).eq('id', requestId);
  };

  const deleteCompDayRequest = async (requestId: string) => {
    setCompDayRequests(prev => prev.filter(r => r.id !== requestId));
    await supabase.from('comp_day_requests').delete().eq('id', requestId);
  };

  return (
    <AuthContext.Provider value={{
      currentUser, currentTenant, isPlatformAdmin: currentUser?.isPlatformAdmin || false, switchTenant,
      loginWithCredentials, confirmLogin, logout,
      users, tenants, updateTenantName, updateTenantModules, updateTenantMargins, updateTenantGeminiKey, addUser, createEmployeeLogin, updateUserRole, updateUserModules, updateUserFinancials,
      changePassword, resetPassword,
      updateUserTimesheet, vacationRequests, createVacationRequest, updateVacationRequestStatus,
      editVacationRequest, deleteVacationRequest,
      timeCorrectionRequests, createTimeCorrectionRequest, updateTimeCorrectionStatus, deleteTimeCorrectionRequest,
      compDayRequests, createCompDayRequest, updateCompDayStatus, editCompDayRequest, deleteCompDayRequest,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
